import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {buildCategorySelfPath} from '../../../../../../libs/knowledge-base/shared/helpers/categoryHelpers';
import {
  buildKnowledgeBaseListPath,
  buildKnowledgeBaseSettingsContentPath,
  buildPublicCategoryUrl,
  buildPublicContactUsUrl,
  buildPublicKnowledgeBaseUrl,
} from '../../../../../../libs/knowledge-base/shared/helpers/urlHelpers';
import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Trans,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  KnowledgeBaseStatusesEnum,
  KnowledgeBaseSupportedLanguagesTypesEnum,
} from '../../../../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {useAppQuery} from '../../../../../../libs/shared/core/src/services/query/useAppQuery';
import {getKnowledgeBases} from '../../../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {Skeleton} from '../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import noResultsImageSrc from '../../../../../assets/images/noResults.png';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AnalyticsDataPointPageViewPageTypesEnum} from '../../../../../models/api/analyticsMetricsModel';
import {knowledgeBaseReportCategoryQueries} from '../queries/knowledgeBaseReportCategoryQueries';

/*
 * Props.
 */

interface KnowledgeBaseReportSidePanelNoPreviewProps {
  knowledgeBaseId: number;
  id: number;
  pageType: AnalyticsDataPointPageViewPageTypesEnum;
  locale?: KnowledgeBaseSupportedLanguagesTypesEnum;
}

/*
 * Style.
 */

const StyledNoPreviewStateWrapperDiv = styled.div`
  display: grid;
  flex: 1;
  padding: 0px 8px;
`;

const StyledNoPreviewStateCenterDiv = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const StyledInfoWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 310px;
  gap: 24px;
`;

const StyledInfoImg = styled.img<{$imageWidth: number; $imageHeight: number}>`
  width: ${(p) => p.$imageWidth}px;
  height: ${(p) => p.$imageHeight}px;
`;

const StyledInfoTextWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StyledInfoHeaderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  line-height: 20px;
  max-width: 480px;
  white-space: normal;
  text-align: center;
`;

const StyledInfoBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: center;
  line-height: 21px;
`;

const StyledActionsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

/*
 * Component.
 */

export const KnowledgeBaseReportSidePanelNoPreview: FC<KnowledgeBaseReportSidePanelNoPreviewProps> = ({
  id,
  knowledgeBaseId,
  pageType,
  locale,
}) => {
  const localize = useLocalize();
  const knowledgeBase = useAppSelector(getKnowledgeBases)[knowledgeBaseId];
  const {data, asyncStatus} = useAppQuery(knowledgeBaseReportCategoryQueries.byId(id, locale));
  const categoryLocaleName = useMemo(() => {
    if (!knowledgeBase || !data) {
      return undefined;
    }
    return data?.names[locale || knowledgeBase.defaultLocale];
  }, [knowledgeBase, data, locale]);
  const {navigateToLink} = useLinkNavigation();

  const renderActions = () => {
    if (asyncStatus === AsyncStatusesEnum.LOADING) {
      return (
        <>
          <Skeleton width="116px" height="30px" borderRadius="9000px" />
          <Skeleton width="116px" height="30px" borderRadius="9000px" />
        </>
      );
    }

    if (data && knowledgeBase) {
      return (
        <>
          {/* Only link to external website if we have a valid translated name otherwise it is not published. */}
          {categoryLocaleName === KnowledgeBaseStatusesEnum.PUBLISHED &&
            renderViewInWebsiteButton(() =>
              navigateToLink(buildPublicCategoryUrl(knowledgeBase, data, locale)),
            )}
          {renderEditPageButton(() =>
            navigateToLink(buildKnowledgeBaseListPath(knowledgeBaseId, buildCategorySelfPath(data), locale)),
          )}
        </>
      );
    }
    if (
      knowledgeBase &&
      (pageType === AnalyticsDataPointPageViewPageTypesEnum.HOME ||
        pageType === AnalyticsDataPointPageViewPageTypesEnum.CONTACT_US)
    ) {
      return (
        <>
          {/* Only link to external website if it is published. */}
          {knowledgeBase.statuses[locale || knowledgeBase.defaultLocale] ===
            KnowledgeBaseStatusesEnum.PUBLISHED &&
            renderViewInWebsiteButton(() => {
              if (pageType === AnalyticsDataPointPageViewPageTypesEnum.CONTACT_US) {
                return navigateToLink(buildPublicContactUsUrl(knowledgeBase, undefined, locale));
              }
              return navigateToLink(buildPublicKnowledgeBaseUrl(knowledgeBase, '/', locale));
            })}
          {renderEditPageButton(() => {
            if (pageType === AnalyticsDataPointPageViewPageTypesEnum.CONTACT_US) {
              return navigateToLink(buildKnowledgeBaseSettingsContentPath(knowledgeBaseId));
            }
            return navigateToLink(buildKnowledgeBaseListPath(knowledgeBaseId, undefined, locale));
          })}
        </>
      );
    }
    // If we failed to load a category it would be deleted so we can't link to anything.
    return null;
  };

  return (
    <StyledNoPreviewStateWrapperDiv>
      <StyledNoPreviewStateCenterDiv>
        <StyledInfoWrapperDiv>
          <StyledInfoImg
            $imageWidth={92}
            $imageHeight={67}
            src={noResultsImageSrc}
            alt={localize(
              defineMessage({
                id: 'analytics-knowledge-base-sidepanel-no-preview-state-image-alt',
                message: 'Can not preview image',
              }),
            )}
          />
          <StyledInfoTextWrapperDiv>
            <StyledInfoHeaderDiv>
              <Trans id="analytics-knowledge-base-sidepanel-no-preview-state-title">
                Page can&apos;t be previewed
              </Trans>
            </StyledInfoHeaderDiv>
            <StyledInfoBodyDiv>
              <Trans id="analytics-knowledge-base-sidepanel-no-preview-state-description">
                Only articles can be previewed.
              </Trans>
            </StyledInfoBodyDiv>
          </StyledInfoTextWrapperDiv>
          <StyledActionsWrapperDiv>{renderActions()}</StyledActionsWrapperDiv>
        </StyledInfoWrapperDiv>
      </StyledNoPreviewStateCenterDiv>
    </StyledNoPreviewStateWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderViewInWebsiteButton(onClick: () => void) {
  return (
    <Button leftIcon="externalLinkSmall" onClick={onClick}>
      <Trans id="analytics-knowledge-base-sidepanel-view-in-website-button">View in website</Trans>
    </Button>
  );
}

function renderEditPageButton(onClick: () => void) {
  return (
    <Button leftIcon="editSmall" onClick={onClick}>
      <Trans id="analytics-knowledge-base-sidepanel-edit-page-button">Edit page</Trans>
    </Button>
  );
}
